import amplitude from 'amplitude-js';

import {AnalyticsService} from 'src/analytics';
import {isInternalUser} from 'src/utils/check-internal-user';
import logger from 'src/utils/logger';

import {BYOD_AMPLITUDE_EVENTS} from './byod-amplitude-constants';


type CommonProps = {
  agency_id: string;
  agency_name: string;
};

function withCommon(props: CommonProps) {
  let internal = 'No';
  try {
    internal = isInternalUser() ? 'Yes' : 'No';
  } catch (error) {
    logger.warn('Failed to determine internal user status', {error});
  }
  return {
    agency_id: props.agency_id,
    agency_name: props.agency_name,
    user_id: amplitude.getInstance().getUserId() ?? '',
    internalUser: internal,
  };
}

/**
 * Generic BYOD tracking helper. Merges common fields (agency, user, internal
 * flag) with event-specific properties and fires an Amplitude event.
 */
function trackByodEvent(
  event: string,
  common: CommonProps,
  extra: Record<string, unknown> = {},
): void {
  AnalyticsService.track(event, {
    ...withCommon(common),
    ...extra,
  });
}

// ---------------------------------------------------------------------------
// Dashboard lifecycle
// ---------------------------------------------------------------------------

export function trackCustomDashboardSaved({
  agency_id,
  agency_name,
  dashboardId,
  dashboardName,
  dashboardUrl,
  reportsCount,
  reports,
}: CommonProps & {
  dashboardId: string;
  dashboardName: string;
  dashboardUrl: string;
  reportsCount: number;
  reports: string;
}): void {
  trackByodEvent(
    BYOD_AMPLITUDE_EVENTS.CUSTOM_DASHBOARD_SAVED,
    {agency_id, agency_name},
    {
      dashboard_id: dashboardId,
      dashboard_name: dashboardName,
      dashboard_url: dashboardUrl,
      reports_count: reportsCount,
      reports,
    },
  );
}

export function trackCustomDashboardCreated({
  agency_id,
  agency_name,
  dashboardName,
}: CommonProps & {
  dashboardName: string;
}): void {
  trackByodEvent(
    BYOD_AMPLITUDE_EVENTS.CUSTOM_DASHBOARD_CREATED,
    {agency_id, agency_name},
    {
      dashboard_name: dashboardName,
    },
  );
}

export function trackCustomDashboardDeleted({
  agency_id,
  agency_name,
  dashboardId,
  dashboardName,
}: CommonProps & {
  dashboardId: string;
  dashboardName: string;
}): void {
  trackByodEvent(
    BYOD_AMPLITUDE_EVENTS.CUSTOM_DASHBOARD_DELETED,
    {agency_id, agency_name},
    {
      dashboard_id: dashboardId,
      dashboard_name: dashboardName,
    },
  );
}

export function trackCustomDashboardEdited({
  agency_id,
  agency_name,
  dashboardId,
  dashboardName,
}: CommonProps & {
  dashboardId: string | null;
  dashboardName: string;
}): void {
  trackByodEvent(
    BYOD_AMPLITUDE_EVENTS.CUSTOM_DASHBOARD_EDITED,
    {agency_id, agency_name},
    {
      dashboard_id: dashboardId ?? '',
      dashboard_name: dashboardName,
    },
  );
}

export function trackCustomDashboardUpdated({
  agency_id,
  agency_name,
  dashboardId,
  dashboardName,
  dashboardUrl,
  reportsCount,
  reports,
}: CommonProps & {
  dashboardId: string;
  dashboardName: string;
  dashboardUrl: string;
  reportsCount: number;
  reports: string;
}): void {
  trackByodEvent(
    BYOD_AMPLITUDE_EVENTS.CUSTOM_DASHBOARD_UPDATED,
    {agency_id, agency_name},
    {
      dashboard_id: dashboardId,
      dashboard_name: dashboardName,
      dashboard_url: dashboardUrl,
      reports_count: reportsCount,
      reports,
    },
  );
}

// ---------------------------------------------------------------------------
// Report lifecycle
// ---------------------------------------------------------------------------

export function trackReportAddedToDashboard({
  agency_id,
  agency_name,
  dashboardId,
  dashboardName,
  reportName,
  reportType,
}: CommonProps & {
  dashboardId: string | null;
  dashboardName: string;
  reportName: string;
  reportType: string;
}): void {
  trackByodEvent(
    BYOD_AMPLITUDE_EVENTS.REPORT_ADDED_TO_DASHBOARD,
    {agency_id, agency_name},
    {
      dashboard_id: dashboardId ?? '',
      dashboard_name: dashboardName,
      report_name: reportName,
      report_type: reportType,
    },
  );
}

export function trackReportRemovedFromDashboard({
  agency_id,
  agency_name,
  dashboardId,
  dashboardName,
  reportName,
}: CommonProps & {
  dashboardId: string | null;
  dashboardName: string;
  reportName: string;
}): void {
  trackByodEvent(
    BYOD_AMPLITUDE_EVENTS.REPORT_REMOVED_FROM_DASHBOARD,
    {agency_id, agency_name},
    {
      dashboard_id: dashboardId ?? '',
      dashboard_name: dashboardName,
      report_name: reportName,
    },
  );
}

// ---------------------------------------------------------------------------
// Navigation & page views
// ---------------------------------------------------------------------------

export function trackScoutVisited({agency_id, agency_name}: CommonProps): void {
  trackByodEvent(BYOD_AMPLITUDE_EVENTS.SCOUT_VISITED, {agency_id, agency_name});
}

export function trackReportsLibraryVisited({
  agency_id,
  agency_name,
}: CommonProps): void {
  trackByodEvent(BYOD_AMPLITUDE_EVENTS.REPORTS_LIBRARY_VISITED, {
    agency_id,
    agency_name,
  });
}

export function trackReportViewed({
  agency_id,
  agency_name,
  reportName,
  reportType,
}: CommonProps & {
  reportName: string;
  reportType?: string;
}): void {
  trackByodEvent(
    BYOD_AMPLITUDE_EVENTS.REPORT_VIEWED,
    {agency_id, agency_name},
    {
      report_name: reportName,
      ...(reportType ? {report_type: reportType} : {}),
    },
  );
}

// ---------------------------------------------------------------------------
// Dashboard builder actions
// ---------------------------------------------------------------------------

export function trackDashboardPinnedToHome({
  agency_id,
  agency_name,
  dashboardId,
  dashboardName,
  dashboardUrl,
  scope,
}: CommonProps & {
  dashboardId: string;
  dashboardName: string;
  dashboardUrl: string;
  scope: 'self' | 'everyone';
}): void {
  trackByodEvent(
    BYOD_AMPLITUDE_EVENTS.DASHBOARD_PINNED_TO_HOME,
    {agency_id, agency_name},
    {
      dashboard_id: dashboardId,
      dashboard_name: dashboardName,
      dashboard_url: dashboardUrl,
      scope,
    },
  );
}

export function trackReportConfigEdited({
  agency_id,
  agency_name,
  dashboardId,
  dashboardName,
  reportName,
}: CommonProps & {
  dashboardId: string | null;
  dashboardName: string;
  reportName: string;
}): void {
  trackByodEvent(
    BYOD_AMPLITUDE_EVENTS.REPORT_CONFIG_EDITED,
    {agency_id, agency_name},
    {
      dashboard_id: dashboardId ?? '',
      dashboard_name: dashboardName,
      report_name: reportName,
    },
  );
}

export function trackDashboardLayoutChanged({
  agency_id,
  agency_name,
  dashboardId,
  dashboardName,
}: CommonProps & {
  dashboardId: string | null;
  dashboardName: string;
}): void {
  trackByodEvent(
    BYOD_AMPLITUDE_EVENTS.DASHBOARD_LAYOUT_CHANGED,
    {agency_id, agency_name},
    {
      dashboard_id: dashboardId ?? '',
      dashboard_name: dashboardName,
    },
  );
}

export function trackDashboardDateRangeChanged({
  agency_id,
  agency_name,
  dashboardId,
  dashboardName,
  startDate,
  endDate,
}: CommonProps & {
  dashboardId: string | null;
  dashboardName: string;
  startDate: string;
  endDate: string;
}): void {
  trackByodEvent(
    BYOD_AMPLITUDE_EVENTS.DASHBOARD_DATE_RANGE_CHANGED,
    {agency_id, agency_name},
    {
      dashboard_id: dashboardId ?? '',
      dashboard_name: dashboardName,
      start_date: startDate,
      end_date: endDate,
    },
  );
}

export function trackReportFiltersApplied({
  agency_id,
  agency_name,
  reportName,
  filterName,
}: CommonProps & {
  reportName: string;
  filterName: string;
}): void {
  trackByodEvent(
    BYOD_AMPLITUDE_EVENTS.REPORT_FILTERS_APPLIED,
    {agency_id, agency_name},
    {
      report_name: reportName,
      filter_name: filterName,
    },
  );
}

export function trackChartStyleChanged({
  agency_id,
  agency_name,
  dashboardId,
  reportName,
  chartStyle,
}: CommonProps & {
  dashboardId?: string | null;
  reportName: string;
  chartStyle: string;
}): void {
  trackByodEvent(
    BYOD_AMPLITUDE_EVENTS.CHART_STYLE_CHANGED,
    {agency_id, agency_name},
    {
      ...(dashboardId ? {dashboard_id: dashboardId} : {}),
      report_name: reportName,
      chart_style: chartStyle,
    },
  );
}

export function trackDashboardSectionAdded({
  agency_id,
  agency_name,
  dashboardId,
  dashboardName,
}: CommonProps & {
  dashboardId: string | null;
  dashboardName: string;
}): void {
  trackByodEvent(
    BYOD_AMPLITUDE_EVENTS.DASHBOARD_SECTION_ADDED,
    {agency_id, agency_name},
    {
      dashboard_id: dashboardId ?? '',
      dashboard_name: dashboardName,
    },
  );
}

export function trackUnsavedChangesExited({
  agency_id,
  agency_name,
  dashboardId,
  dashboardName,
  exitAction,
}: CommonProps & {
  dashboardId: string | null;
  dashboardName: string;
  exitAction: 'discard_and_leave' | 'discard_edit_mode';
}): void {
  trackByodEvent(
    BYOD_AMPLITUDE_EVENTS.UNSAVED_CHANGES_EXITED,
    {agency_id, agency_name},
    {
      dashboard_id: dashboardId ?? '',
      dashboard_name: dashboardName,
      exit_action: exitAction,
    },
  );
}
